import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { BreadcrumbSchema } from '@/components/SchemaOrg';
import { Heart, Filter, ChevronDown } from 'lucide-react';
import MainHeader from '@/components/MainHeader';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Product {
  id: string;
  name: string;
  slug: string;
  price: number;
  original_price?: number;
  images: string[];
  status: string;
  categories: string[];
  subcategories: string[];
}

const categoryNames: Record<string, string> = {
  inchiostri: 'Inchiostri',
  bobine: 'Bobine',
  colla: 'Colla',
  ricambi: 'Ricambi',
};

// Slug che rappresentano una *sottocategoria* (menu dropdown):
// in questi casi vogliamo mostrare i prodotti della categoria padre già filtrati.
const SLUG_TO_CATEGORY_AND_SUBCATEGORY: Record<
  string,
  { categoryName: string; subcategory?: string }
> = {
  dtf: { categoryName: 'Inchiostri', subcategory: 'DTF' },
  'dtf-uv': { categoryName: 'Inchiostri', subcategory: 'DTF UV' },
  'dtf-uv-soft': { categoryName: 'Inchiostri', subcategory: 'DTF UV SOFT' },

  'bobine-dtf': { categoryName: 'Bobine', subcategory: 'BOBINE DTF' },
  'bobine-dtf-uv': { categoryName: 'Bobine', subcategory: 'BOBINE DTF UV' },
  'bobine-speciali': { categoryName: 'Bobine', subcategory: 'BOBINE SPECIALI' },

  'colla-dtf': { categoryName: 'Colla', subcategory: 'COLLA DTF' },

  testine: { categoryName: 'Ricambi', subcategory: 'TESTINE' },
  componenti: { categoryName: 'Ricambi', subcategory: 'COMPONENTI' },
};

// Mappatura sottocategorie per categoria (usata per i filtri)
const SUBCATEGORIES_BY_CATEGORY: Record<string, string[]> = {
  Inchiostri: ['DTF', 'DTF UV', 'DTF UV SOFT'],
  Bobine: ['BOBINE DTF', 'BOBINE DTF UV', 'BOBINE SPECIALI'],
  Colla: ['COLLA DTF'],
  Ricambi: ['TESTINE', 'COMPONENTI'],
};

const ProductCard = ({ product }: { product: Product }) => {
  const image =
    product.images?.[0] ||
    'https://images.unsplash.com/photo-1589739900243-4b52cd9b104e?w=400&h=400&fit=crop';
  const isOnSale = product.original_price && product.original_price > product.price;
  const isOutOfStock = product.status === 'esaurito';

  return (
    <Link
      to={`/prodotto/${product.slug}`}
      className="group bg-card rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <div className="relative aspect-square bg-muted overflow-hidden">
        <img
          src={image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />

        {isOnSale && (
          <span className="absolute top-3 left-3 px-3 py-1 text-xs font-bold uppercase rounded bg-destructive text-destructive-foreground">
            OFFERTA
          </span>
        )}
        {isOutOfStock && (
          <span className="absolute top-3 left-3 px-3 py-1 text-xs font-bold uppercase rounded bg-muted-foreground text-background">
            ESAURITO
          </span>
        )}

        <button
          className="absolute top-3 right-3 w-8 h-8 bg-background rounded-full flex items-center justify-center shadow hover:bg-primary hover:text-primary-foreground transition-colors"
          onClick={(e) => {
            e.preventDefault();
          }}
        >
          <Heart className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-card-foreground mb-2 line-clamp-2">
          {product.name}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-primary">€{product.price.toFixed(2)} <span className="text-xs font-normal text-muted-foreground">IVA esclusa</span></span>
          {product.original_price && (
            <span className="text-sm text-muted-foreground line-through">
              €{product.original_price.toFixed(2)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const Categoria = () => {
  const { slug } = useParams<{ slug: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('tutti');

  const slugKey = (slug || '').toLowerCase();
  const slugCfg = SLUG_TO_CATEGORY_AND_SUBCATEGORY[slugKey];

  const categoryName = slug
    ? slugCfg?.categoryName || categoryNames[slugKey] || slug
    : 'Tutti i Prodotti';

  // Ottieni le sottocategorie disponibili per la categoria corrente
  const availableSubcategories = SUBCATEGORIES_BY_CATEGORY[categoryName] || [];

  const forcedSubcategory = slugCfg?.subcategory;

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      let query = supabase
        .from('products')
        .select('id, name, slug, price, original_price, images, status, categories, subcategories');

      if (slug && slugKey !== 'tutti') {
        query = query.contains('categories', [categoryName]);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching products:', error);
      } else {
        let filteredProducts = (data || []) as Product[];
        
        // Filtra per sottocategoria se selezionata
        if (selectedSubcategory !== 'tutti') {
          filteredProducts = filteredProducts.filter(p => 
            p.subcategories?.includes(selectedSubcategory)
          );
        }
        
        // Ordina
        switch (sortBy) {
          case 'price-asc':
            filteredProducts = filteredProducts.sort((a, b) => a.price - b.price);
            break;
          case 'price-desc':
            filteredProducts = filteredProducts.sort((a, b) => b.price - a.price);
            break;
          case 'name':
            filteredProducts = filteredProducts.sort((a, b) => a.name.localeCompare(b.name));
            break;
          default:
            break;
        }
        
        setProducts(filteredProducts);
      }
      setLoading(false);
    };

    fetchProducts();
  }, [slug, categoryName, sortBy, selectedSubcategory]);

  // Reset / pre-seleziona la sottocategoria quando cambia pagina (slug)
  useEffect(() => {
    setSelectedSubcategory(forcedSubcategory || 'tutti');
  }, [slug, forcedSubcategory]);

  return (
    <>
      <SEOHead
        title={`${categoryName} | Materialidtf.it`}
        description={`Scopri la nostra selezione di ${categoryName.toLowerCase()} per stampa DTF. Qualità professionale, spedizione rapida.`}
        canonical={`/categoria/${slug || 'tutti'}`}
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: categoryName, url: `/categoria/${slug || 'tutti'}` },
        ]}
      />

      <MainHeader />
      <Navigation />

      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{categoryName}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground uppercase tracking-wide">
              {categoryName}
            </h1>

            <div className="flex items-center gap-4 flex-wrap">
              {/* Filtro sottocategorie - mostra solo se ci sono sottocategorie disponibili */}
              {availableSubcategories.length > 0 && (
                <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sottocategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutti">Tutte</SelectItem>
                    {availableSubcategories.map((sub) => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Ordina per" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Più recenti</SelectItem>
                  <SelectItem value="price-asc">Prezzo: crescente</SelectItem>
                  <SelectItem value="price-desc">Prezzo: decrescente</SelectItem>
                  <SelectItem value="name">Nome A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="aspect-square rounded-xl" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">
                Nessun prodotto trovato in questa categoria.
              </p>
              <Link
                to="/categoria/tutti"
                className="inline-block mt-4 text-primary hover:underline font-semibold"
              >
                Vedi tutti i prodotti
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
};

export default Categoria;
